import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useAccount } from '../contexts/AccountContext';
import { firestore } from '../services/firebase';
import type { CollectionArchetype } from '../types/knowledge';

export interface KnowledgeDocument {
  docId: string;
  fileName: string;
  sourceUrl: string;
  storageType: 'gcs' | 'sharepoint';
  collectionType: CollectionArchetype;
  syncStatus: 'pending' | 'processing' | 'ready' | 'error';
  globalSummary?: string;
  updatedAt: string;
}

export function useKnowledgeDocuments(collectionType: CollectionArchetype) {
  const { account } = useAccount();
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!account?.clientId) {
      setLoading(false);
      return;
    }

    const docsRef = collection(firestore, 'clients', account.clientId, 'knowledge_documents');
    const q = query(docsRef, where('collectionType', '==', collectionType));
    setLoading(true);
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map(
          (doc) =>
            ({
              ...(doc.data() as Omit<KnowledgeDocument, 'docId'>),
              docId: doc.id,
            }) as KnowledgeDocument
        );
        // Sort by updatedAt desc locally since we might not have a composite index set up yet
        docs.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        setDocuments(docs);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [account?.clientId, collectionType]);

  return { documents, loading, error };
}
